import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { Box, Flex, Text, Button, Stack, Radio, RadioGroup, RangeSlider, RangeSliderTrack, RangeSliderFilledTrack, RangeSliderThumb, useBreakpointValue, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, Icon, } from "@chakra-ui/react";
import { MdCheck } from "react-icons/md";
const FILTER_CATEGORIES = [
    { key: "availability", label: "Availability" },
    { key: "passType", label: "Pass Type" },
    { key: "sortBy", label: "Sort By" },
    { key: "priceRange", label: "Price Range" },
];
const AVAILABILITY_OPTIONS = [
    { label: "Fast Filling", value: "ACTIVE" },
    { label: "Coming Soon", value: "COMING_SOON" },
    { label: "Sold Out", value: "SOLD_OUT" },
];
const PASS_TYPE_OPTIONS = ["STAG", "LADIES", "COUPLE"];
const SORT_OPTIONS = [
    { value: "low", label: "Price: Low To High" },
    { value: "high", label: "Price: High To Low" },
];
const PassesFilterDrawer = ({ isOpen, onClose, passes, initialFilters, onApply, onClearAll, }) => {
    // Local filter state
    const [selectedCategory, setSelectedCategory] = useState("availability");
    const [availability, setAvailability] = useState(initialFilters.availability || []);
    const [passType, setPassType] = useState(initialFilters.passType || []);
    const [sortBy, setSortBy] = useState(initialFilters.sortBy || "");
    const [priceRange, setPriceRange] = useState(initialFilters.priceRange);
    // Compute min/max price from passes
    const minPrice = passes.length
        ? Math.min(...passes.map((p) => p.booking_prices.cover + p.booking_prices.entry))
        : 0;
    const maxPrice = passes.length
        ? Math.max(...passes.map((p) => p.booking_prices.cover + p.booking_prices.entry))
        : 10000;
    // Count options for each filter
    const filterCounts = useMemo(() => ({
        availability: AVAILABILITY_OPTIONS.length,
        passType: PASS_TYPE_OPTIONS.length,
        sortBy: SORT_OPTIONS.length,
        priceRange: 1,
    }), []);
    // Count active filters per category
    const activeCounts = {
        availability: availability.length,
        passType: passType.length,
        sortBy: sortBy !== "" ? 1 : 0,
        priceRange: priceRange[0] !== minPrice || priceRange[1] !== maxPrice ? 1 : 0,
    };
    // Add a function to calculate pass counts
    const getPassCounts = (passes) => {
        const counts = {
            ACTIVE: 0,
            COMING_SOON: 0,
            SOLD_OUT: 0,
        };
        passes.forEach((pass) => {
            if (pass.status in counts) {
                counts[pass.status]++;
            }
        });
        return counts;
    };
    // Add function to count passes by type
    const getPassTypeCounts = (passes) => {
        const counts = {
            STAG: 0,
            LADIES: 0,
            COUPLE: 0,
        };
        passes.forEach((pass) => {
            if (pass.pass_type in counts) {
                counts[pass.pass_type]++;
            }
        });
        return counts;
    };
    // Render right panel options
    const renderOptions = () => {
        switch (selectedCategory) {
            case "availability":
                const passCounts = getPassCounts(passes);
                return (_jsx(Stack, { spacing: 4, children: AVAILABILITY_OPTIONS.map((option) => {
                        const isSelected = availability.includes(option.value);
                        return (_jsxs(Flex, { align: "center", justify: "space-between", cursor: "pointer", py: 1, borderRadius: "md", fontSize: "xs", fontWeight: 400, onClick: () => {
                                setAvailability((prev) => isSelected
                                    ? prev.filter((v) => v !== option.value)
                                    : [...prev, option.value]);
                            }, children: [_jsxs(Flex, { align: "center", gap: 3, children: [isSelected && (_jsx(Icon, { as: MdCheck, boxSize: 5, color: "brand" })), _jsx(Text, { color: "white", children: option.label })] }), _jsx(Text, { color: "white", children: passCounts[option.value] })] }, option.value));
                    }) }));
            case "passType":
                const passTypeCounts = getPassTypeCounts(passes);
                return (_jsx(Stack, { spacing: 4, children: PASS_TYPE_OPTIONS.map((option) => {
                        const label = option.charAt(0) + option.slice(1).toLowerCase();
                        const isSelected = passType.includes(option);
                        return (_jsxs(Flex, { align: "center", justify: "space-between", cursor: "pointer", py: 1, borderRadius: "md", fontSize: "xs", fontWeight: 400, onClick: () => {
                                setPassType((prev) => isSelected
                                    ? prev.filter((v) => v !== option)
                                    : [...prev, option]);
                            }, children: [_jsxs(Flex, { align: "center", gap: 3, children: [isSelected && (_jsx(Icon, { as: MdCheck, boxSize: 5, color: "brand" })), _jsx(Text, { color: "white", children: label })] }), _jsx(Text, { color: "white", children: passTypeCounts[option] })] }, option));
                    }) }));
            case "sortBy":
                return (_jsx(RadioGroup, { value: sortBy, onChange: (val) => setSortBy(val), children: _jsx(Stack, { spacing: 6, children: SORT_OPTIONS.map((opt) => (_jsx(Radio, { value: opt.value, colorScheme: "purple", children: _jsx(Text, { fontSize: "xs", children: opt.label }) }, opt.value))) }) }));
            case "priceRange":
                return (_jsxs(Box, { w: "100%", children: [_jsx(Text, { fontSize: "xs", fontWeight: "400", mb: 4, children: "Select Price Range" }), _jsxs(Text, { fontSize: "md", fontWeight: "600", mr: 2, mb: 4, children: ["\u20B9", priceRange[0], " - \u20B9", priceRange[1]] }), _jsxs(RangeSlider, { min: minPrice, max: maxPrice, step: 100, value: priceRange, onChange: (val) => setPriceRange(val), minStepsBetweenThumbs: 1, flex: 1, colorScheme: "purple", "aria-label": ["min", "max"], children: [_jsx(RangeSliderTrack, { children: _jsx(RangeSliderFilledTrack, {}) }), _jsx(RangeSliderThumb, { index: 0 }), _jsx(RangeSliderThumb, { index: 1 })] })] }));
            default:
                return null;
        }
    };
    // Responsive width
    const leftColWidth = useBreakpointValue({ base: "35%", md: "200px" });
    return (_jsxs(Drawer, { isOpen: isOpen, placement: "right", onClose: onClose, size: "lg", children: [_jsx(DrawerOverlay, {}), _jsxs(DrawerContent, { bg: "#111111", color: "white", children: [_jsx(DrawerHeader, { borderBottom: "1px solid #3E3E3E", children: _jsxs(Flex, { align: "center", justify: "space-between", py: 4, children: [_jsx(Text, { fontWeight: "bold", fontSize: "lg", children: "Filters" }), _jsx(Button, { variant: "link", color: "#A383FF", fontWeight: "bold", onClick: onClearAll, fontSize: "sm", children: "Clear All" })] }) }), _jsx(DrawerBody, { p: 0, children: _jsxs(Flex, { flex: 1, h: "100%", children: [_jsx(Box, { bg: "#1E1E1E", w: leftColWidth, minW: "120px", h: "100%", borderRight: "1px solid #3E3E3E", py: 4, children: _jsx(Stack, { spacing: 2, children: FILTER_CATEGORIES.map((cat) => (_jsxs(Flex, { align: "center", px: 4, py: 2, cursor: "pointer", bg: selectedCategory === cat.key ? "#181818" : "transparent", borderLeft: selectedCategory === cat.key
                                                ? "3px solid #A383FF"
                                                : "3px solid transparent", fontWeight: selectedCategory === cat.key ? "normal" : 400, color: selectedCategory === cat.key ? "#A383FF" : "white", onClick: () => setSelectedCategory(cat.key), fontSize: "xs", position: "relative", children: [_jsx(Text, { flex: 1, children: cat.label }), activeCounts[cat.key] > 0 && (_jsx(Text, { ml: 2, color: "brand", fontSize: "xs", fontWeight: 400, children: activeCounts[cat.key] }))] }, cat.key))) }) }), _jsx(Box, { flex: 1, px: 6, py: 5, overflowY: "auto", children: renderOptions() })] }) }), _jsxs(Flex, { borderTop: "1px solid #414141", px: 6, py: 4, justify: "space-between", align: "center", bg: "#232222", gap: 6, children: [_jsx(Button, { variant: "outline", color: "white", bg: "#191919", borderColor: "#303030", fontSize: "xs", fontWeight: 400, onClick: onClose, minW: "120px", w: "100%", children: "Close" }), _jsx(Button, { colorScheme: "purple", bg: "brand", color: "white", borderRadius: "4", fontSize: "xs", px: 8, fontWeight: 400, minW: "120px", w: "100%", onClick: () => onApply({ availability, passType, sortBy, priceRange }), children: "Apply" })] })] })] }));
};
export default PassesFilterDrawer;
